/*
  Restores the always-on / default-valued <head> meta tags that `pages/_app.tsx`
  rendered before the SA-6876 refactor, matching the old code exactly.

  - <title>            falls back to TITLE when the page has none (per-page title
                       still rendered by MetaData)
  - description        on every page except the 'ignore' sentinel, fallback DESCRIPTION
  - robots             defaults to noindex when the page doesn't set it
  - OG block           the COMPLETE old block: when `og` exists, every tag is
                       rendered with its old `|| fallback`, exactly like the old
                       _app.tsx. This is the SINGLE source of OG tags (MetaData and
                       AppGeoMetadata no longer render OG), so there are no duplicates.
  - product:price:*    currency -> 'USD', amount -> '0.00'

  (viewport, canonical and the hreflang alternates live in AppGeoMetadata; the
  global JSON-LD lives in AppGlobalSchema.)
*/

import React, { FC } from 'react'
import Head from 'next/head'
import { NextRouter } from 'next/router'
import { DESCRIPTION, OGIMAGE, TITLE } from 'components/MetaData/constants'
import { createNewURL } from 'lib/storeLocale/client'
import { getGeoLocationMetaData } from 'selectors/user/cookie' // TODO - Move to Locale
import { getPagePath } from 'selectors/page'
import { urlQuery } from 'lib/helpers'
import parse from 'lib/url/parse'
import { PageInitialProps } from 'types/metadata'
import { User } from 'types/User'
import { Shared } from 'types/Shared'
import { Page } from 'types/Page'
import { getPageTitles } from './pageTitle'

/* We need a way to have clean path for homepage ( Product ASK )
   Ex: `.com/` => we want `.com`
   This helper will only clean up specific rule
*/
const cleanHomePage = (url: string): string => {
  return url.match(/com[/]$/) ? url.replace(/\/$/, '') : url
}

type InitialState = {
  user: User
  shared: Shared
  page: Page
}

type AppMetaFallbacksProps = {
  initialProps?: PageInitialProps
  initialState: InitialState
  router?: NextRouter
}

const AppMetaFallbacks: FC<AppMetaFallbacksProps> = ({ initialProps, initialState, router }) => {
  const { title, description, og, product, robots } = initialProps?.metaData || {}

  const { storeLocale: currentStoreLocale } = getGeoLocationMetaData(initialState)
  const urlPath = router?.asPath || getPagePath(initialState)

  // Create a storeLocale valid url w/ only PAGE as a valid query param for the canonical
  const result = parse(urlPath)
  const { origin, pathname: path, query } = result
  let queryParams = ''

  let cleanPath = path
  /* Remove trailing SLASH for SEO
    Ex: `.com/stories/` => `.com/stories`
    We don't want duplicate content
  */
  if (cleanPath.match(/.*[/]$/)) {
    cleanPath = cleanPath.replace(/\/$/, '')
  }

  if (query.page) {
    queryParams = urlQuery({
      page: query.page,
    })
  }

  const serverCanonicalUrl = `${origin}${cleanPath}${queryParams}` // The server does NOT send back `storeLocale` - we will need to add manually
  const canonicalUrl = cleanHomePage(createNewURL(serverCanonicalUrl, currentStoreLocale))

  // Old code always rendered <title>/og:title with a TITLE fallback (plus the
  // `| Page N` suffix on paginated routes).
  const { pageTitle, ogPageTitle } = getPageTitles(
    initialState,
    TITLE,
    og?.title || TITLE,
    query.page
  )

  return (
    <Head>
      {/* Title — old always rendered, falling back to TITLE */}
      {!title && <title key='title'>{pageTitle}</title>}

      {/* Description — old rendered on every page except the 'ignore' sentinel */}
      {description !== 'ignore' && !description && (
        <meta name='description' key='description' content={DESCRIPTION} />
      )}

      {/* Robots — old always rendered; defaults to noindex when not provided */}
      {typeof robots !== 'boolean' && (
        <meta
          name='robots'
          key='robots'
          content='noindex, nofollow, max-image-preview:large, noai, noimageai'
        />
      )}

      {/* OG TAGS — verbatim old block: every tag rendered with its `|| fallback`
          whenever `og` exists. Single source of OG tags. */}
      {og && (
        <>
          <meta key='og-site_name' property='og:site_name' content='Saatchi Art' />
          <meta key='og-type' property='og:type' content={og.type || 'article'} />
          <meta key='og-image' property='og:image' content={og.image || OGIMAGE} />
          <meta key='og-title' property='og:title' content={ogPageTitle} />
          <meta
            key='og-description'
            property='og:description'
            content={og.description || DESCRIPTION}
          />
          <meta key='og-url' property='og:url' content={canonicalUrl} />
          <meta key='og-locale' property='og:locale' content={og.locale || currentStoreLocale} />
          <meta key='og-locale-alternate' property='og:locale:alternate' content='en_us' />

          {og.availability && (
            <meta key='og-availability' property='og:availability' content={og.availability} />
          )}
        </>
      )}

      {/* Product price fallbacks — old defaulted currency/amount */}
      {product?.price && (
        <>
          {!product.price.currency && (
            <meta key='product-price-currency' property='product:price:currency' content='USD' />
          )}
          {!product.price.amount && (
            <meta key='product-price-amount' property='product:price:amount' content='0.00' />
          )}
        </>
      )}
    </Head>
  )
}

export default AppMetaFallbacks
